import { useEffect, useState } from 'react';
import { MdInfoOutline, MdSend } from 'react-icons/md';

import { Avatar, SegmentedControl } from '@/components';
import { Button, ButtonOutlined } from '@/components/Button';
import { Autocomplete, ContactInput, Input } from '@/components/Inputs';
import { guestContactOptions, pickedGuestContacts } from '@/store/contacts/contacts.mappers';
import { useContacts } from '@/store/contacts/contacts.store';

import s from '../EventWizardModal.module.scss';

/** Someone already in the viewer's contacts, or anyone else, reached by a link. */
type InviteMode = 'contacts' | 'link';
const INVITE_MODES = [
  { value: 'contacts', label: 'Contacts' },
  { value: 'link', label: 'Invite by link' },
] as const;

interface InvitePanelProps {
  name: string;
  onNameChange: (value: string) => void;
  contact: string;
  onContactChange: (value: string) => void;
  /** Empties the typed name and contact; the panel itself never closes. */
  onClear: () => void;
  /** Whom to invite — the picked contacts, or the guest typed in — by the open tab. */
  onSend: (recipients: { name: string; contact: string; personId?: string }[]) => void;
  /** Person id → why that contact can't be picked here (already chosen elsewhere). */
  unavailable: Record<string, string>;
}

export function InvitePanel({
  name,
  onNameChange,
  contact,
  onContactChange,
  onClear,
  onSend,
  unavailable,
}: InvitePanelProps) {
  const contacts = useContacts((state) => state.contacts);
  const getContacts = useContacts((state) => state.getContacts);
  const [query, setQuery] = useState('');
  const [pickedIds, setPickedIds] = useState<string[]>([]);
  const [mode, setMode] = useState<InviteMode>('contacts');

  useEffect(() => {
    void getContacts();
  }, [getContacts]);

  const contactOptions = guestContactOptions(contacts);
  const search = query.trim().toLowerCase();
  const options = contactOptions
    .filter((option) => !pickedIds.includes(option.value))
    .filter((option) => option.name.toLowerCase().includes(search))
    .map((option) => {
      const taken = unavailable[option.personId];
      return {
        value: option.value,
        disabled: option.disabled || !!taken,
        label: (
          <>
            <Avatar src={option.avatar} firstName={option.name} size={28} />
            {taken ? `${option.name} — ${taken}` : option.label}
          </>
        ),
      };
    });
  const selectedOptions = contactOptions
    .filter((option) => pickedIds.includes(option.value))
    .map((option) => ({ value: option.value, label: option.name }));

  const byContacts = mode === 'contacts';
  const canSend = byContacts ? pickedIds.length > 0 : !!name.trim() && !!contact.trim();
  const isEmpty = byContacts ? pickedIds.length === 0 && !query : !name && !contact;

  // The panel stays open after sending, so the open tab starts over empty for the next guest.
  const clear = () => {
    if (byContacts) {
      setPickedIds([]);
      setQuery('');
    } else {
      onClear();
    }
  };

  return (
    <div className={s.invitePanel}>
      <SegmentedControl
        size="sm"
        as="radio"
        options={INVITE_MODES}
        value={mode}
        onChange={setMode}
        aria-label="Whom to invite"
        className={s.inviteModes}
      />
      {byContacts ? (
        <Autocomplete
          label="Contacts"
          placeholder={pickedIds.length > 0 ? undefined : 'Choose contacts'}
          value={query}
          onChange={setQuery}
          options={options}
          selectedOptions={selectedOptions}
          onMultipleItemsSelect={(selected) => {
            setPickedIds(selected.map(({ value }) => value));
            setQuery('');
          }}
        />
      ) : (
        <>
          <p className={s.inviteSub}>Your guest will use a link in email or SMS to join.</p>
          <Input
            label="Guest Name"
            mandatory
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Alex Morgan"
          />
          <ContactInput
            label="Mobile or Email"
            value={contact}
            onChange={onContactChange}
            mandatory
          />
        </>
      )}
      <div className={s.expiryNote}>
        <MdInfoOutline />
        <span>The link expires 1 day before the voyage starts.</span>
      </div>
      <div className={s.inviteActions}>
        <ButtonOutlined onClick={clear} disabled={isEmpty}>
          Clear
        </ButtonOutlined>
        <Button
          onClick={() => {
            onSend(
              byContacts
                ? pickedGuestContacts(contacts, pickedIds)
                : [{ name: name.trim(), contact: contact.trim() }],
            );
            clear();
          }}
          disabled={!canSend}
        >
          Send Invitation <MdSend />
        </Button>
      </div>
    </div>
  );
}
